import { useCallback, useState } from 'react';

import {
  AdminSectionCard,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonBlock,
  Switch,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { SEED_AGENT_REVIEW_INLINE_CONFIG } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  agentReviewInlineStatusLabel,
  nextAgentReviewInlineConfig,
} from './AgentReviewInlineScene.transform';

export function AgentReviewInlineScene() {
  const [config, setConfig] = useState(SEED_AGENT_REVIEW_INLINE_CONFIG);
  const [loading] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleEnabled = useCallback(() => {
    setToggleBusy(true);
    setError(null);
    setConfig((prev) => nextAgentReviewInlineConfig(prev));
    setToggleBusy(false);
  }, []);

  return (
    <AdminSceneShell
      sceneName="AgentReviewInlineScene"
      title="Agent review (inline)"
      description="Show inline agent-review cards in chat threads."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-3"
    >
      <Commentable name="Panel">
        <div className="space-y-3">
          <AdminSectionCard
            title="Agent review (inline)"
            description="Controls inline agent write stamping and collab build-stamp gating on the editor surface. GA default is ON — use this toggle to opt out."
            actions={
              loading ? (
                <SkeletonBlock className="h-5 w-9 rounded-full" />
              ) : (
                <>
                  <Pill tone={config.enabled ? 'success' : 'default'}>
                    {agentReviewInlineStatusLabel(config.enabled)}
                  </Pill>
                  <Switch
                    id="agent-review-inline-enabled"
                    checked={config.enabled}
                    onCheckedChange={toggleEnabled}
                    disabled={toggleBusy}
                    aria-label="Agent review (inline)"
                  />
                </>
              )
            }
          >
            {config.enabled ? (
              <p className="text-sm text-muted">
                <strong className="font-medium text-zinc-900 dark:text-zinc-100">
                  ON
                </strong>{' '}
                — inline agent write stamping and collab build-stamp gating are active on
                the editor surface.
              </p>
            ) : (
              <p className="text-sm text-muted">
                <strong className="font-medium text-zinc-900 dark:text-zinc-100">
                  OFF
                </strong>{' '}
                — inline stamping and build-stamp gating are disabled.
              </p>
            )}
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
              Toggling affects already-open editor tabs. Clients with a stale or missing
              build stamp receive read-only collab tokens until they reload.
            </p>
          </AdminSectionCard>

          {error ? <InlineError>{error}</InlineError> : null}
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
